import { useState } from "react";
import { api, apiError } from "../lib/api";
import { X } from "lucide-react";
import { toast } from "sonner";

const MARKS = ["Present", "Absent", "Late", "Left early", "Not with student"];

export default function ClassroomLogSheet({ classroom, block, students, onClose, onSaved }) {
  const existing = block.log;
  const [groupNote, setGroupNote] = useState(existing?.group_note || "");
  const [marks, setMarks] = useState(existing?.attendance_marks || {});
  const [notes, setNotes] = useState(existing?.student_notes || {});
  const [openNotes, setOpenNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!groupNote.trim()) { toast.error("A brief group note is required"); return; }
    setSaving(true);
    try {
      await api.post("/classroom-logs", {
        classroom_id: classroom.id, date: new Date().toISOString().slice(0, 10),
        block_id: block.block_id, block_name: block.name, block_time: `${block.start}–${block.end}`,
        group_note: groupNote, attendance_marks: marks, student_notes: notes,
      });
      toast.success(`${block.name} logged for ${classroom.name}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="classroom-log-sheet" className="sf-sheet bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-heading font-semibold text-[#132238]">{block.name} — {classroom.name}</h2>
            <p className="text-xs text-[#667085]">{block.start}–{block.end} · one log for the room, student notes only when needed</p>
          </div>
          <button data-testid="close-classroom-log-btn" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[13px] font-semibold text-[#132238]">How did the block go? (required)</label>
            <textarea data-testid="group-note-input" value={groupNote} onChange={(e) => setGroupNote(e.target.value)} rows={2}
              placeholder="e.g., Smooth morning meeting; group stayed engaged with the calendar activity."
              className="mt-1.5 w-full rounded-[10px] border border-[#E5E9EE] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
          </div>
          <div className="space-y-2.5">
            <p className="text-[13px] font-semibold text-[#132238]">Students — mark exceptions only</p>
            {students.map((s) => (
              <div key={s.id} className="border border-[#E5E9EE] rounded-xl p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-medium text-[#132238]">{s.name}</p>
                  <button data-testid={`toggle-note-${s.id}`} onClick={() => setOpenNotes((o) => ({ ...o, [s.id]: !o[s.id] }))}
                    className="text-[11px] font-semibold text-[#087F5B]">{notes[s.id] || openNotes[s.id] ? "note ✓" : "+ note"}</button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {MARKS.map((m) => (
                    <button key={m} data-testid={`mark-${s.id}-${m.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setMarks((x) => ({ ...x, [s.id]: m }))}
                      className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium border transition-colors duration-150 ${
                        (marks[s.id] || "Present") === m ? (m === "Present" ? "bg-[#EAF8F3] text-[#066548] border-[#C8EFE2]" : "bg-amber-50 text-amber-700 border-amber-300")
                          : "bg-white text-[#98A2B3] border-[#E5E9EE] hover:bg-slate-50"}`}>{m}</button>
                  ))}
                </div>
                {(openNotes[s.id] || notes[s.id]) && (
                  <input data-testid={`student-note-${s.id}`} value={notes[s.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [s.id]: e.target.value }))}
                    placeholder="Student-specific note (only if needed)"
                    className="mt-2 w-full rounded-lg border border-[#E5E9EE] px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-slate-100 p-4 sticky bottom-0 bg-white">
          <button data-testid="save-classroom-log-btn" onClick={submit} disabled={saving}
            className="w-full bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
            {saving ? "Saving…" : existing ? "Update Classroom Log" : "Save Classroom Log"}
          </button>
        </div>
      </div>
    </div>
  );
}
